<template>
  <v-container fluid class="rq-page">
    <!-- ================= Header ================= -->
    <div class="rq-header">
      <a class="rq-back" @click="getBackList()">
        <v-icon size="20" color="#2A6F97">mdi-arrow-left</v-icon>
        <span class="ml-2">Back to time off</span>
      </a>
      <h1 class="rq-title">Request time off</h1>
      <div class="rq-subtitle">Plan your leave and send it to your manager for approval</div>
    </div>

    <div class="rq-grid">
      <!-- ================= Left: Leave details ================= -->
      <div class="rq-card rq-form-card">
        <div class="rq-form-body">
          <div class="rq-card-title">Leave details</div>
          <div class="rq-muted-sm">Fields marked with <span class="rq-req">*</span> are required</div>

          <!-- Leave type -->
          <div class="rq-label mt-6">Leave type <span class="rq-req">*</span></div>
          <v-select v-model="leave_data.leaveTypeId" :items="dropdownLeave" item-text="leave_type"
            item-value="leave_type_id" placeholder="Select leave type" outlined dense hide-details class="rq-input"
            :menu-props="{ offsetY: true }" @change="onLeaveTypeChange"></v-select>
          <div v-if="leave_data.leaveTypeId != 0 && textshow" class="rq-help">{{ textshow }}</div>

          <!-- Request duration -->
          <div class="rq-label mt-6">Request duration</div>
          <div class="rq-toggle">
            <button type="button" class="rq-toggle-btn" :class="{ 'rq-toggle-active': day_select === 'single' }"
              @click="setDaySelect('single')">
              <v-icon size="20" :color="day_select === 'single' ? '#FFFFFF' : '#4B5563'">mdi-calendar-blank</v-icon>
              <span class="ml-2">Single day</span>
            </button>
            <button type="button" class="rq-toggle-btn" :class="{ 'rq-toggle-active': day_select === 'multiple' }"
              @click="setDaySelect('multiple')">
              <v-icon size="20" :color="day_select === 'multiple' ? '#FFFFFF' : '#4B5563'">mdi-calendar-range</v-icon>
              <span class="ml-2">Multiple days</span>
            </button>
          </div>

          <!-- Date (start) + Duration -->
          <div class="rq-row mt-5">
            <div class="rq-col">
              <div class="rq-label">{{ day_select === "single" ? "Date" : "From" }} <span class="rq-req">*</span></div>
              <v-menu ref="menu_start" v-model="menu_start" :close-on-content-click="false"
                transition="scale-transition" offset-y min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field :value="displayDate(date_start)" append-icon="mdi-calendar" readonly v-bind="attrs"
                    v-on="on" outlined dense hide-details class="rq-input"></v-text-field>
                </template>
                <v-date-picker v-model="date_start" no-title scrollable :allowed-dates="allowedDates"
                  @change="checkHoliday(date_start, 'start')">
                  <v-btn text color="primary" @click="menu_start = false">Cancel</v-btn>
                  <v-btn text color="primary" @click="$refs.menu_start.save(date_start)">OK</v-btn>
                </v-date-picker>
              </v-menu>
            </div>
            <div class="rq-col">
              <div class="rq-label">
                {{ day_select === "single" ? "Duration" : "First day duration" }} <span class="rq-req">*</span>
              </div>
              <v-select v-model="leave_start_hours" :items="leave_hours" placeholder="Select duration" outlined dense
                hide-details class="rq-input" :menu-props="{ offsetY: true }"></v-select>
            </div>
          </div>

          <!-- Date (end) - multiple days -->
          <div v-if="day_select == 'multiple'" class="rq-row mt-4">
            <div class="rq-col">
              <div class="rq-label">To <span class="rq-req">*</span></div>
              <v-menu ref="menu_end" v-model="menu_end" :close-on-content-click="false" transition="scale-transition"
                offset-y min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field :value="displayDate(date_end)" append-icon="mdi-calendar" readonly v-bind="attrs"
                    v-on="on" outlined dense hide-details class="rq-input"></v-text-field>
                </template>
                <v-date-picker v-model="date_end" no-title scrollable :allowed-dates="allowedDates"
                  @change="checkHoliday(date_end, 'end')">
                  <v-btn text color="primary" @click="menu_end = false">Cancel</v-btn>
                  <v-btn text color="primary" @click="$refs.menu_end.save(date_end)">OK</v-btn>
                </v-date-picker>
              </v-menu>
            </div>
            <div class="rq-col">
              <div class="rq-label">Last day duration <span class="rq-req">*</span></div>
              <v-select v-model="leave_end_hours" :items="leave_hours" placeholder="Select duration" outlined dense
                hide-details class="rq-input" :menu-props="{ offsetY: true }"></v-select>
            </div>
          </div>

          <!-- Requested / Balance after -->
          <div class="rq-info-bar mt-4">
            <div class="d-flex align-center">
              <div class="rq-info-icon">
                <v-icon size="18" color="#FFFFFF">mdi-calendar-blank</v-icon>
              </div>
              <span class="ml-3">Requested:</span>
              <span class="rq-accent ml-2">{{ formatHours(requestedHours) }}</span>
            </div>
            <div class="rq-info-sep"></div>
            <div class="d-flex align-center">
              <span>Balance after request:</span>
              <span class="rq-accent rq-accent-lg ml-2" :class="{ 'rq-over': isOverBalance }">{{ balanceAfterText
                }}</span>
            </div>
          </div>

          <v-divider class="rq-divider my-5"></v-divider>

          <!-- Reason -->
          <div class="rq-label">Reason</div>
          <v-textarea v-model="leave_data.description"
            placeholder="Tell your manager why you're requesting time off (optional)" outlined rows="3" hide-details
            class="rq-input rq-textarea"></v-textarea>

          <!-- Attachment -->
          <div class="rq-label mt-6">Attachment</div>
          <div class="rq-drop" :class="{ 'rq-drop-over': dragOver }" @click="$refs.file.click()"
            @dragover.prevent="dragOver = true" @dragleave.prevent="dragOver = false" @drop.prevent="onDropFiles">
            <v-icon size="44" color="#2A6F97">mdi-cloud-upload-outline</v-icon>
            <div class="ml-5">
              <div class="rq-drop-title">Drop a file here or <a class="rq-link">browse</a></div>
              <div class="rq-muted-sm">JPG or PNG images</div>
            </div>
          </div>
          <input type="file" accept="image/*" multiple ref="file" style="display: none" @change="previewMultiImage" />

          <!-- Preview รูปที่แนบ (คลิกเพื่อลบ) -->
          <div v-if="preview_list.length" class="rq-preview-list">
            <div v-for="(item, index) in preview_list" :key="index" class="rq-preview-item"
              @click="deleteItemConfirm(index)">
              <img :src="item" />
              <div class="rq-preview-remove">
                <v-icon size="16" color="#FFFFFF">mdi-close</v-icon>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer buttons (เงื่อนไขเดิม) -->
        <div v-if="user_approve_count_status && user_approve_count_status2" class="rq-form-footer">
          <v-btn outlined depressed class="rq-btn rq-btn-outline" @click="getBackList()">Cancel</v-btn>
          <v-btn depressed color="#2A6F97" class="rq-btn rq-btn-primary ml-4" @click="onSave()">Submit request</v-btn>
        </div>
      </div>

      <!-- ================= Right column ================= -->
      <div class="rq-side">
        <!-- Request summary -->
        <div class="rq-card rq-side-card">
          <div class="rq-card-title mb-5">Request summary</div>
          <div class="rq-sum-row">
            <span>Leave type</span><span>{{ selectedLeaveTypeName }}</span>
          </div>
          <div class="rq-sum-row">
            <span>Date</span><span>{{ summaryDateText }}</span>
          </div>
          <div class="rq-sum-row">
            <span>Duration</span><span>{{ summaryDurationText }}</span>
          </div>
          <div class="rq-sum-row">
            <span>Total</span><b>{{ formatHours(requestedHours) }}</b>
          </div>
          <v-divider class="rq-divider my-4"></v-divider>
          <div class="rq-sum-row">
            <span>Balance after request</span>
            <span class="rq-accent rq-accent-lg" :class="{ 'rq-over': isOverBalance }">{{ balanceAfterText }}</span>
          </div>
          <div class="rq-note mt-4">
            <v-icon size="18" color="#2A6F97">mdi-information-outline</v-icon>
            <span class="ml-2">Your manager will be notified after submission.</span>
          </div>
        </div>

        <!-- Your time off -->
        <div class="rq-card rq-side-card mt-5">
          <div class="rq-card-title mb-5">Your time off</div>
          <div class="rq-yto">
            <div class="rq-yto-item">
              <v-icon size="34" color="#2A6F97">mdi-calendar-blank</v-icon>
              <div class="rq-yto-label">Available</div>
              <div class="rq-yto-value" style="color: #2A6F97;">
                {{ vacation_day }} <span class="rq-yto-unit">{{ Number(vacation_day) === 1 ? "day" : "days" }}</span>
                <span v-if="Number(vacation_hours) > 0" class="rq-yto-sub">{{ vacation_hours }} hrs</span>
              </div>
            </div>
            <div class="rq-yto-sep"></div>
            <div class="rq-yto-item">
              <v-icon size="34" color="#E08A1E">mdi-clock-outline</v-icon>
              <div class="rq-yto-label">Pending</div>
              <div class="rq-yto-value" style="color: #E08A1E;">
                {{ pending_cont }} <span class="rq-yto-unit">{{ Number(pending_cont) === 1 ? "request" : "requests"
                  }}</span>
              </div>
            </div>
            <div class="rq-yto-sep"></div>
            <div class="rq-yto-item">
              <v-icon size="34" color="#4B5563">mdi-calendar-blank</v-icon>
              <div class="rq-yto-label">Upcoming</div>
              <div class="rq-yto-value" style="color: #4B5563;">
                {{ upcoming90.days }} <span class="rq-yto-unit">{{ upcoming90.days === 1 ? "day" : "days" }}</span>
                <span v-if="upcoming90.hours > 0" class="rq-yto-sub">{{ upcoming90.hours }} hrs</span>
              </div>
            </div>
          </div>
        </div>

        <a class="rq-link rq-history-link mt-5" @click="getBackList()">
          View request history <v-icon size="18" color="#2A6F97">mdi-arrow-right</v-icon>
        </a>
      </div>
    </div>

    <!-- ================= Dialogs (เดิม) ================= -->
    <v-dialog v-model="dialogDelete" max-width="500px">
      <v-card style="padding-top: 30px; padding-bottom: 30px;">
        <v-card-title style="padding-bottom: 20px;">
          <v-row justify="center">
            <span class="red--text text-h5 ju">Are you sure?</span>
          </v-row>
        </v-card-title>
        <br />
        <v-card-subtitle>
          <v-row justify="center">
            <span class="black--text">Are you sure you want to delete this item</span>
          </v-row>
        </v-card-subtitle>
        <v-card-actions style="padding-top: 20px">
          <v-spacer></v-spacer>
          <v-btn style="
              color: white;
            " rounded class="mr-5" color="#0A7EA5" width="170" @click="removeimage">
            Save
          </v-btn>
          <v-btn style="
              color: white;
            " class="mr-2" color="red" width="170" rounded @click="closeDelete">
            Cancel
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogSave" max-width="500px">
      <v-card>
        <v-card-title class="text-h5">Are you sure ?</v-card-title>
        <br />
        <v-card-subtitle>
          <span class="black--text">Do you really want to send this leave request? This process cannot
            be undone.</span>
        </v-card-subtitle>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="closeSave">Cancel</v-btn>
          <v-btn color="blue darken-1" text @click="onSave">OK</v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <SuccessDialog :status="dialogAdd" :text_color="text_color" :title="title" :message="message" />

    <SuccessDialogPush :status="dialogAdd" :text_color="text_color" :title="title" :message="message" :link="link" />
  </v-container>
</template>

<script>
import api from "@/services/api";
import { server } from "@/services/constants";
import SuccessDialog from "@/components/cards/SuccessDialog";
import SuccessDialogPush from "@/components/cards/SuccessDialogPush";
import "@lazy-copilot/datetimepicker/dist/datetimepicker.css";
import { DateTimePicker } from "@lazy-copilot/datetimepicker";
import unity from "@/unity/unity";
import moment from "moment";

export default {
  name: "leave-request",
  components: { SuccessDialog, SuccessDialogPush, DateTimePicker },
  async beforeCreate() {
    console.log("beforeCreate");
    this.$store.state.navMenu = true;
  },
  data(vm) {
    return {
      day_select: "single",
      select_leave_id: 0,
      user_approve_all: [],
      user_approve_count_status: false,
      user_approve_count_status2: false,
      date_start: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10),
      date_end: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10),
      menu_start: false,
      menu_end: false,
      leave_start_hours: "",
      leave_end_hours: "",
      leave_start_min: "",
      leave_end_min: "",
      leave_hours: [
        { text: "1 hour", value: 1 },
        { text: "2 hours", value: 2 },
        { text: "3 hours", value: 3 },
        { text: "Half day", value: 4 },
        { text: "5 hours", value: 5 },
        { text: "6 hours", value: 6 },
        { text: "7 hours", value: 7 },
        { text: "Full day", value: 8 },
      ],
      leave_min: [
        { text: "00", value: "00" },
        { text: "30", value: "30" },
      ],
      startDate: new Date(),
      endDate: new Date(),
      dialogDelete: false,
      dialogSave: false,
      preview: null,
      image: null,
      preview_list: [],
      oldpreview_list: [],
      image_list: [],
      menu: false,
      menu2: false,
      search: "",
      toggleEnable: false,
      selectstatus: "",
      dropdownLeave: [],
      dataAll: [],
      headers: [
        {
          text: "Period",
          value: "period",
          class: "bg-colorth tabletextwhite",
        },
        {
          text: "Leave Type",
          value: "leave_type",
          class: "bg-colorth tabletextwhite",
        },
        // {
        //   text: "Quota (Day)",
        //   value: "quota_day_show",
        //   class: "bg-colorth tabletextwhite",
        // },
        // {
        //   text: "Used",
        //   value: "user_day",
        //   class: "bg-colorth tabletextwhite",
        // },
        {
          text: "Quota (Day)",
          value: "balance_show",
          class: "bg-colorth tabletextwhite",
        },
      ],
      leave_data: {
        leaveType: 0,
        leaveTypeId: 0,
        startDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
          .toISOString()
          .substr(0, 10),
        endDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
          .toISOString()
          .substr(0, 10),
        description: "",
        userId: 0,
        departmentId: 0,
        companyId: 0,
        amount: 0,
        images: null,
        lq_id: 0,
      },
      dateCurrent: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10),
      dialogAdd: false,
      text_color: "text-h5 green--text text-center",
      title: "green",
      message: "green",
      link: "",
      vacation_day: 0,
      vacation_hours: 0,
      approver_day: 0,
      approver_hours: 0,
      pending_cont: 0,
      public_cont: 0,
      textshow: "",
      // ===== UI ใหม่ =====
      dragOver: false, // ลากไฟล์มาวางบนช่อง Attachment
      holiday_dates: [], // วันหยุดบริษัท (YYYY-MM-DD) ใช้คำนวณวันลา / Upcoming
      leaveHistoryAll: [], // ประวัติการลา ใช้คำนวณ Upcoming 90 วัน
    };
  },
  computed: {
    // ===== ชื่อประเภทลาที่เลือก =====
    selectedLeaveTypeName() {
      const t = this.dropdownLeave.find((x) => x.leave_type_id === this.leave_data.leaveTypeId);
      return t ? t.leave_type : "-";
    },
    // ===== ชั่วโมงที่ขอลา (คำนวณแสดงผลเท่านั้น ตอนบันทึกยังใช้ logic เดิมใน onSave) =====
    requestedHours() {
      const startH = Number(this.leave_start_hours) || 0;
      if (this.day_select === "single") return startH;
      const endH = Number(this.leave_end_hours) || 0;
      if (!this.date_start || !this.date_end || this.date_end <= this.date_start) return startH;
      const wd = this.countWeekdays(this.date_start, this.date_end);
      if (wd <= 1) return startH;
      // เหมือน onSave: วันเต็มตรงกลาง x 8 + ชั่วโมงวันแรก + ชั่วโมงวันสุดท้าย
      return (wd - 2) * 8 + startH + endH;
    },
    // ===== โควต้าคงเหลือของประเภทลาที่เลือก (ชั่วโมง) =====
    selectedBalanceHours() {
      if (!this.leave_data.leaveTypeId) return null;
      const q = this.dataAll.filter((x) => x.leave_type_id === this.leave_data.leaveTypeId);
      if (!q.length) return 0;
      return Number(q[0].balance || 0) * 8;
    },
    balanceAfterText() {
      if (this.selectedBalanceHours === null) return "-";
      const after = Math.round((this.selectedBalanceHours - this.requestedHours) * 100) / 100;
      if (after < 0) return "-" + this.formatHours(Math.abs(after));
      return this.formatHours(after, true);
    },
    // ลาเกินโควต้าคงเหลือ -> แสดงสีแดง
    isOverBalance() {
      return this.selectedBalanceHours !== null && this.requestedHours > this.selectedBalanceHours;
    },
    summaryDateText() {
      if (this.day_select === "single") return this.displayDate(this.date_start);
      return `${this.displayDate(this.date_start)} - ${this.displayDate(this.date_end)}`;
    },
    summaryDurationText() {
      const txt = (v) => {
        const o = this.leave_hours.find((x) => x.value === v);
        return o ? o.text : "-";
      };
      if (this.day_select === "single") return txt(this.leave_start_hours);
      return `${txt(this.leave_start_hours)} / ${txt(this.leave_end_hours)}`;
    },
    // ===== Upcoming: วันลาที่อนุมัติแล้ว ภายใน 90 วันข้างหน้า (เหมือนหน้า My time off) =====
    upcoming90() {
      const today = moment().startOf("day");
      const limit = moment(today).add(90, "days");
      let totalHours = 0;
      this.leaveHistoryAll.forEach((item) => {
        if (!this.isApproved(item)) return;
        const { start, end: rawEnd } = this.leaveRange(item);
        if (!start) return;
        const end = rawEnd || start;
        if (end.isBefore(today, "day") || start.isAfter(limit, "day")) return;
        const hours = Number(item.hours_amount) || 0;
        const allDays = this.countWeekdays(start, end);
        const inDays = this.countWeekdays(moment.max(start, today), moment.min(end, limit));
        totalHours += allDays > 0 ? (hours * inDays) / allDays : 0;
      });
      totalHours = Math.round(totalHours * 100) / 100;
      const days = Math.floor(totalHours / 8);
      const hours = Math.round((totalHours - days * 8) * 100) / 100;
      return { days, hours };
    },
    computedDateFromFormatted() {
      return unity.formatDate(this.date_start);
    },
    computedDateToFormatted() {
      return unity.formatDate(this.date_end);
    },
    allowedDates() {
      return (date) => {
        date = new Date(date);
        const dayOfWeek = date.getDay();
        return dayOfWeek !== 0 && dayOfWeek !== 6; // Disable Sunday (0) and Saturday (6)
      };
    },
  },
  watch: {
    dialog(val) {
      val || this.close();
    },
    dialogDelete(val) {
      val || this.closeDelete();
    },
  },

  async mounted() {
    await api.checkVersion();
    this.userId = localStorage.getItem(server.USER_ID);

    await this.loaddataLeaveUsed(this.userId);

    //เช็คว่ามีคน Approve หรือไม่
    await this.loadLevelApprove(this.userId);
    await this.loadQuotaType();
    this.$hideLoader();
  },

  methods: {
    onLeaveTypeChange() {
      // this.textshow = "test";

      if (this.dataAll && this.dataAll.length > 0) {
        const dataSet = this.dataAll.filter(
          (item) => item.leave_type_id === this.leave_data.leaveTypeId,
        );
        if (dataSet && dataSet.length == 1) {
          this.textshow = `${dataSet[0].balance_show} available this year`;
        } else if (dataSet && dataSet.length > 1) {
          this.textshow = `${dataSet[0].balance_show} available this year, ${dataSet[1].balance_show} available next year`;
        } else {
          this.textshow = `0 days available this year`;
        }
      } else {
      }
    },
    async loadQuotaType() {
      const res_leave_history = await api.getListHistoryAll(
        localStorage.getItem(server.USER_ID),
      );

      if (res_leave_history.data && res_leave_history.data.length > 0) {
        const filteredData = res_leave_history.data.filter(
          (item) => Number(item.status) === 1 || Number(item.status) === 2,
        );
        this.pending_cont = filteredData.length;
      } else {
        this.pending_cont = 0;
      }
      this.leaveHistoryAll = res_leave_history.data || [];

      const res_quota = await api.getListByUserDashboard(
        localStorage.getItem(server.USER_ID),
      );
      if (res_quota.data && res_quota.data.length > 0) {
        var canuse_leave = 0;
        canuse_leave = Number(res_quota.data[0].leave_can_use);
        this.vacation_day = Math.floor(canuse_leave);
        this.vacation_hours = (canuse_leave - this.vacation_day) * 8;
      } else {
        this.vacation_day = 0;
        this.vacation_hours = 0;
      }

      const res_holiday = await api.getAllYear(
        Number(localStorage.getItem(server.COMPANYID)),
      );
      if (res_holiday.data && res_holiday.data.length > 0) {
        this.public_cont = res_holiday.data.length;
        this.holiday_dates = res_holiday.data.map((h) => this.holidayDateKey(h)).filter(Boolean);
      } else {
        this.public_cont = 0;
        this.holiday_dates = [];
      }

      const res_approval = await api.getAllByApproval(
        Number(localStorage.getItem(server.USER_ID)),
      );
      if (res_approval.data && res_approval.data.length > 0) {

        this.approver_day = Math.floor(
          Number(res_approval.data[0].amount) / 60 / 8,
        );
        this.approver_hours = Math.floor(
          (Number(res_approval.data[0].amount) - this.approver_day * 60 * 8) /
          60,
        );
      } else {
        this.approver_day = 0;
        this.approver_hours = 0;
      }
    },
    // ================= UI helpers (ใหม่) =================
    setDaySelect(val) {
      this.day_select = val;
      this.handleRadioDaySelect();
    },
    // 2026-09-07 -> 7 Sep 2026
    displayDate(ymd) {
      if (!ymd) return "";
      const m = moment(ymd, "YYYY-MM-DD");
      return m.isValid() ? m.format("D MMM YYYY") : ymd;
    },
    // ชั่วโมง -> "1 day" / "4 hours" / "2 days 4 hours"
    formatHours(total, showZero) {
      const t = Number(total) || 0;
      if (t <= 0) return showZero ? "0 days" : "-";
      const d = Math.floor(t / 8);
      const h = Math.round((t - d * 8) * 100) / 100;
      const parts = [];
      if (d > 0) parts.push(`${d} ${d === 1 ? "day" : "days"}`);
      if (h > 0) parts.push(`${h} ${h === 1 ? "hour" : "hours"}`);
      return parts.join(" ");
    },
    // ลากไฟล์มาวาง -> ใช้ previewMultiImage เดิม
    onDropFiles(e) {
      this.dragOver = false;
      if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length) {
        this.previewMultiImage({ target: { files: e.dataTransfer.files } });
      }
    },
    // นับวันทำงาน (ไม่นับ เสาร์-อาทิตย์ และวันหยุดบริษัท)
    countWeekdays(start, end) {
      let n = 0;
      const d = moment(start).startOf("day");
      const e = moment(end).startOf("day");
      while (d.isSameOrBefore(e, "day")) {
        const isWeekend = d.day() === 0 || d.day() === 6;
        if (!isWeekend && !this.holiday_dates.includes(d.format("YYYY-MM-DD"))) n++;
        d.add(1, "day");
      }
      return n;
    },
    holidayDateKey(h) {
      const raw = h.h_date || h.holiday_date || h.hdate || h.date || h.start_date || h.h_start;
      const m = this.parseDate(raw);
      return m ? m.format("YYYY-MM-DD") : null;
    },
    parseDate(value) {
      if (!value) return null;
      const s = String(value).trim();
      if (/^\d{2}\/\d{2}\/\d{4}/.test(s)) {
        const m = moment(s.substring(0, 10), "DD/MM/YYYY");
        return m.isValid() ? m : null;
      }
      const m = moment(s);
      return m.isValid() ? m : null;
    },
    leaveRange(item) {
      let start = this.parseDate(item.str_time || item.startDate || item.start_date);
      let end = this.parseDate(item.end_time || item.endDate || item.end_date);
      if (!start && item.data_date) {
        const [a, b] = String(item.data_date).split(" - ");
        start = this.parseDate(a);
        end = this.parseDate(b);
      }
      return { start, end };
    },
    isApproved(item) {
      return Number(item.status) === 3 || String(item.status_name || "").toLowerCase().includes("approve");
    },

    async getBackList() {
      this.$router.push(`/my-time-off`);
    },
    async handleRadioDaySelect() {
      // alert(this.day_select)
    },
    async loadLevelApprove(userId) {
      const res_acc = await api.getAccountid(userId);
      // console.log("res_acc", JSON.stringify(res_acc.data));

      if (
        res_acc.data[0].approver_level1 !== null ||
        res_acc.data[0].approver_level1 !== "" ||
        res_acc.data[0].approver_level1 !== 0
      ) {
        this.user_approve_count_status = true;
        this.user_approve_all.push(res_acc.data[0].approver_level1);
      } else {
        // this.user_approve_count_status = true;
        this.user_approve_count_status = false;

        this.$store.state.global_dialog = true;
        this.setupAlertDialog(
          true,
          "ไม่มีผู้อนุมัติ",
          "คุณไม่มีผู้อนุมัติ กรุณาเพิ่มผู้อนุมัติด้วย",
          "text-h5 red--text text-center",
        );
        return;
      }

      if (
        res_acc.data[0].approver_level2 !== null ||
        res_acc.data[0].approver_level2 !== "" ||
        res_acc.data[0].approver_level2 !== 0
      ) {
        this.user_approve_count_status = true;
        this.user_approve_all.push(res_acc.data[0].approver_level2);
      }

      if (
        res_acc.data[0].approver_level3 !== null ||
        res_acc.data[0].approver_level3 !== "" ||
        res_acc.data[0].approver_level3 !== 0
      ) {
        this.user_approve_count_status = true;
        this.user_approve_all.push(res_acc.data[0].approver_level3);
      }
    },

    async checkHoliday(date, mode) {
      // alert(date)
      const res_date = await api.getLeaveHolidayByDate(
        date,
        Number(localStorage.getItem(server.COMPANYID)),
      );

      if (res_date.data && res_date.data.length > 0) {
        this.$store.state.global_dialog = true;
        this.setupAlertDialog(
          true,
          "Check your information again",
          `ไม่สามารถเลือกได้เนื่องจาก ${unity.formatDate(date)} เป็นวันหยุด ${res_date.data[0].h_name
          }`,
          "text-h5 red--text text-center",
        );

        if (mode == "start") {
          this.date_start = new Date(
            Date.now() - new Date().getTimezoneOffset() * 60000,
          )
            .toISOString()
            .substr(0, 10);
        } else if (mode == "end") {
          this.date_end = new Date(
            Date.now() - new Date().getTimezoneOffset() * 60000,
          )
            .toISOString()
            .substr(0, 10);
        } else {
          return;
        }
      }
    },

    async onSave() {
      this.dialogSave = false;

      //validate ข้อมูล
      if (this.leave_data.leaveTypeId == 0) {
        this.$store.state.global_dialog = true;
        this.setupAlertDialog(
          true,
          "Check your information again",
          "กรุณาเลือกประเภทการลา",
          "text-h5 red--text text-center",
        );
        return;
      }
      if (
        this.leave_start_hours === null ||
        this.leave_start_hours === "" ||
        this.leave_start_hours === 0
      ) {
        this.$store.state.global_dialog = true;
        this.setupAlertDialog(
          true,
          "Check your information again",
          "Please select a time period ",
          "text-h5 red--text text-center",
        );
        return;
      }

      const res_date_start = await api.getLeaveHolidayByDate(
        this.date_start,
        Number(localStorage.getItem(server.COMPANYID)),
      );

      // console.log(`getLeaveHolidayByDate start date: ${this.date_start}`);
      // console.log("res_date_start.data");
      // console.log(res_date_start.data);
      if (res_date_start.data != null && res_date_start.data != undefined) {
        if (res_date_start.data.length > 0) {
          this.$store.state.global_dialog = true;
          this.setupAlertDialog(
            true,
            "Check your information again",
            `ไม่สามารถเลือกได้เนื่องจาก ${unity.formatDate(
              this.date_start,
            )} เป็นวันหยุด ${res_date_start.data[0].h_name}`,
            "text-h5 red--text text-center",
          );
          this.date_start = new Date(
            Date.now() - new Date().getTimezoneOffset() * 60000,
          )
            .toISOString()
            .substr(0, 10);
          return;
        }
      }

      if (this.day_select == "multiple") {
        if (this.date_start >= this.date_end) {
          this.$store.state.global_dialog = true;
          this.setupAlertDialog(
            true,
            "Check your information again",
            "คุณเลือกช่วงเวลาไม่ถูกต้อง",
            "text-h5 red--text text-center",
          );
          return;
        }
        const res_date_end = await api.getLeaveHolidayByDate(
          this.date_end,
          Number(localStorage.getItem(server.COMPANYID)),
        );
        if (res_date_end.data && res_date_end.data.length > 0) {
          this.$store.state.global_dialog = true;
          this.setupAlertDialog(
            true,
            "Check your information again",
            `ไม่สามารถเลือกได้เนื่องจาก ${unity.formatDate(
              this.date_end,
            )} เป็นวันหยุด ${res_date_end.data[0].h_name}`,
            "text-h5 red--text text-center",
          );
          this.date_end = new Date(
            Date.now() - new Date().getTimezoneOffset() * 60000,
          )
            .toISOString()
            .substr(0, 10);
          return;
        }

        if (
          this.leave_end_hours === null ||
          this.leave_end_hours === "" ||
          this.leave_end_hours === 0
        ) {
          this.$store.state.global_dialog = true;
          this.setupAlertDialog(
            true,
            "Check your information again",
            "Please select a time period ",
            "text-h5 red--text text-center",
          );
          return;
        }
      }

      //รวมเวลา start end
      if (this.day_select == "single") {
        this.leave_data.startDate = this.date_start + " 08:30";
        this.leave_data.endDate = this.date_start + " 17:30";
      } else if (this.day_select == "multiple") {
        this.leave_data.startDate = this.date_start + " 08:30";
        this.leave_data.endDate = this.date_end + " 17:30";
      } else {
        return;
      }
      //end validate ข้อมูล

      //เช็ค เวลาตรงกับช่วงเวลาที่มีหรือไหม
      let res_chk_period = [];
      if (this.day_select == "single") {
        res_chk_period = await api.getAllCheckPeriodByLeaveType(
          localStorage.getItem(server.USER_ID),
          this.date_start,
          this.date_start,
          this.leave_data.leaveTypeId,
        );
        // console.log("period_data full s:", JSON.stringify(period_data));
      } else if (this.day_select == "multiple") {
        res_chk_period = await api.getAllCheckPeriodByLeaveType(
          localStorage.getItem(server.USER_ID),
          this.date_start,
          this.date_end,
          this.leave_data.leaveTypeId,
        );

        // console.log("period_data full m :", JSON.stringify(period_data));
      } else {
        console.log("period_data full no");
        return;
      }

      // console.log("res_chk_period");
      // console.log(res_chk_period.data);
      if (!res_chk_period.data || res_chk_period.data.length == 0) {
        this.$store.state.global_dialog = true;
        this.setupAlertDialog(
          true,
          "Check your information again",
          "วันลา ไม่อยู่ในช่วงที่กำหนด",
          "text-h5 red--text text-center",
        );
        return;
      }
      //end การเช็คช่วงเวลา

      let period_data = [];
      period_data = res_chk_period.data;


      const millisecondsInOneDay = 24 * 60 * 60 * 1000;

      // let startDate = new Date(this.date_start);
      // let endDate = new Date(this.date_end);

      // console.log(`period_data : ${JSON.stringify(period_data)}`);

      let sum_avg_minutes = 0;
      let avg_minutes_list = [];
      let leave_quota_list = "";

      if (this.day_select == "single") {
        //start คำนวณวันลา
        let avg_minutes = 0; //เวลาที่ลา เป็นนาที
        avg_minutes = Number(this.leave_start_hours) * 60;
        //end คำนวณวันลา

        // //start เช็คโคต้าวันลา
        var quota_amt = 0; //โควต้าวันลาเป็นนาที

        this.dataAll.forEach((item) => {
          // console.log(
          //   `item.id : ${item.id} // leave_quota_id : ${period_data[0].leave_quota_id}`,
          // );

          if (Number(item.id) == Number(period_data[0].leave_quota_id)) {
            this.leave_data.lq_id = Number(period_data[0].leave_quota_id);
            quota_amt = Number(item.balance) * 480;
          }
        });

        // console.log(`dataAll : ${JSON.stringify(this.dataAll)}`);
        // console.log(`this.leave_data : ${JSON.stringify(this.leave_data)}`);

        // console.log("leave single day");
        // console.log(quota_amt);
        // console.log(avg_minutes);

        // console.log(
        //   `leave single day avg_minutes: ${avg_minutes} quota_amt: ${quota_amt}`,
        // );

        if (avg_minutes > quota_amt) {
          this.$store.state.global_dialog = true;
          this.setupAlertDialog(
            true,
            "ลาเกิน",
            "คุณลาเกินวันลาที่มี",
            "text-h5 red--text text-center",
          );
          return;
        }
        //end เช็คโคต้าวันลา

        //start เพิ่มข้อมูลลาไม่เกิน1วัน
        this.leave_data.amount = avg_minutes;
        this.leave_data.userId = localStorage.getItem(server.USER_ID);
        this.leave_data.departmentId = localStorage.getItem(
          server.DEPARTMENT_ID,
        );
        this.leave_data.companyId = localStorage.getItem(server.COMPANYID);

        this.$showLoader();

        // console.log("leave_data:", JSON.stringify(this.leave_data));
        let formData = new FormData();

        this.image_list.forEach((x, index) => {
          formData.append(`image`, x);
        });

        const imageNames = this.image_list
          .map((x, index) => `${x.name}`)
          .join(',');

        formData.append("images", imageNames);

        formData.append("leaveType", this.leave_data.leaveTypeId.toString());
        formData.append("leaveTypeId", this.leave_data.leaveTypeId.toString());
        formData.append("approve_id_now", this.user_approve_all[0]);
        formData.append(
          "startDate",
          this.leave_data.startDate.replace("+07:00", ""),
        );
        formData.append("startHour", this.leave_start_hours);
        formData.append(
          "endDate",
          this.leave_data.endDate.replace("+07:00", ""),
        );
        formData.append("endHour", 0);
        formData.append("type_count", this.day_select);
        formData.append("description", this.leave_data.description);
        formData.append("userId", this.leave_data.userId);
        formData.append("departmentId", this.leave_data.departmentId);
        formData.append("companyId", this.leave_data.companyId);
        formData.append("amount", this.leave_data.amount);
        formData.append("lq_id", this.leave_data.lq_id);

        const res_h = await api.addLeaveHistory(formData);
        if (res_h.status == 200 || res_h.status == 201) {
          for await (const [index, item] of this.user_approve_all.entries()) {
            var data = {
              leave_his_id: res_h.data.module_id,
              approve_id: item,
            };
            if (item != 0 && item != null) {
              await api.addLeaveApproveDetail(data);
            }
            if (index == 0) {
              api.SendnotificationToMobile({
                user_id: item,
                send_mode: "send_to_approver",
              });
            }
          }

          this.$store.state.global_push_dialog = true;
          this.setupAlertDialogPush(
            true,
            "Request Submitted",
            "You have submitted this request.",
            "text-h5 green--text text-center",
            "/my-time-off",
          );
        } else {
          this.$store.state.global_dialog = true;
          this.setupAlertDialog(
            true,
            "Information Saved",
            "บันทึกข้อมูลไม่สำเร็จ",
            "text-h5 red--text text-center",
          );
        }
        this.$hideLoader();
        //end เพิ่มข้อมูลลาไม่เกิน1วัน
      } else if (this.day_select == "multiple") {
        let quota_check = 0;
        for (const [index, item] of period_data.entries()) {
          let avg_minutes = 0;

          // let startDateMany = new Date(item.leave_date_start);
          // let endDateMany = new Date(item.leave_date_end);
          let startDateMany = new Date(this.date_start);
          let endDateMany = new Date(this.date_end);

          while (startDateMany <= endDateMany) {
            const dayOfWeekMany = startDateMany.getDay();

            // Check if the day is not Saturday (6) or Sunday (0)
            if (dayOfWeekMany !== 6 && dayOfWeekMany !== 0) {
              var data_check = startDateMany.toISOString().substr(0, 10);
              var res_date_check = await api.getLeaveHolidayByDate(
                data_check,
                Number(localStorage.getItem(server.COMPANYID)),
              );
              if (res_date_check.data && res_date_check.data.length == 0) {
                avg_minutes += 8 * 60;
              }
            }

            startDateMany.setTime(
              startDateMany.getTime() + millisecondsInOneDay,
            );
          }

          //start บวกขอบ ของวัน
          if (period_data.length == 1) {
            avg_minutes =
              avg_minutes -
              2 * 8 * 60 +
              Number(this.leave_start_hours) * 60 +
              Number(this.leave_end_hours) * 60;
          } else if (period_data.length > 1) {
            if (index == 0) {
              avg_minutes =
                avg_minutes - 1 * 8 * 60 + Number(this.leave_start_hours) * 60;
            }
            if (index == period_data.length - 1) {
              avg_minutes =
                avg_minutes - 1 * 8 * 60 + Number(this.leave_end_hours) * 60;
            }
          } else {
            return;
          }
          //end บวกขอบ ของวัน

          //start เช็คโคต้าวันลา
          var quota_amt = 0; //โควต้าวันลาเป็นนาที

          // this.dataAll.forEach((item) => {
          //   if (item.id == period_data[index].leave_quota_id) {
          //     this.leave_data.lq_id = period_data[index].leave_quota_id;
          //     quota_amt = item.balance * 480;
          //   }
          // });


          // ✅ เปลี่ยนชื่อตัวแปรใน forEach ไม่ให้ชนกัน
          this.dataAll.forEach((dataItem) => {
            if (Number(dataItem.id) == Number(period_data[index].leave_quota_id)) {
              // เปลี่ยน leave_quota_id → id
              this.leave_data.lq_id = Number(period_data[index].leave_quota_id); // เปลี่ยน leave_quota_id → id
              quota_amt = Number(dataItem.balance) * 480;
            }
          });


          if (avg_minutes > quota_amt) {
            quota_check = 1;
          }
          //end เช็คโคต้าวันลา

          // console.log("avg_minutes: " + avg_minutes)
          sum_avg_minutes += avg_minutes;
          // if (index > 0) {
          //   leave_quota_list += `,${item.leave_quota_id}`;
          // } else {
          //   leave_quota_list += `${item.leave_quota_id}`;
          // }

          // ✅ แก้ใน leave_quota_list
          if (index > 0) {
            leave_quota_list += `,${item.leave_quota_id}`; // เปลี่ยน leave_quota_id → id
          } else {
            leave_quota_list += `${item.leave_quota_id}`; // เปลี่ยน leave_quota_id → id
          }

          // avg_minutes_list.push({
          //   leave_quota_id: item.leave_quota_id,
          //   avg_minutes: avg_minutes,
          // });

          // ✅ แก้ใน avg_minutes_list
          avg_minutes_list.push({
            leave_quota_id: item.leave_quota_id, // เปลี่ยน leave_quota_id → id
            avg_minutes: avg_minutes,
          });
        }

        // console.log("sum_avg_minutes: " + sum_avg_minutes / 60 / 8);
        // console.log(avg_minutes_list);

        // alert(quota_check)
        if (quota_check == 1) {
          this.$store.state.global_dialog = true;
          this.setupAlertDialog(
            true,
            "ลาเกิน",
            "คุณลาเกินวันลาที่มี",
            "text-h5 red--text text-center",
          );
          return;
        }

        //     console.log("avg_minutes_list: ")
        //     console.log(sum_avg_minutes)
        //     console.log(leave_quota_list)
        //     console.log(avg_minutes_list)

        //     //start เพิ่มข้อมูลลาเกิน1วัน
        this.leave_data.amount = sum_avg_minutes;
        this.leave_data.userId = localStorage.getItem(server.USER_ID);
        this.leave_data.departmentId = localStorage.getItem(
          server.DEPARTMENT_ID,
        );
        this.leave_data.companyId = localStorage.getItem(server.COMPANYID);

        this.$showLoader();

        // console.log("leave_data:", JSON.stringify(this.leave_data));
        let formData = new FormData();

        this.image_list.forEach((x, index) => {
          formData.append(`image`, x);
        });

        const imageNames = this.image_list
          .map((x, index) => `${x.name}`)
          .join(',');

        formData.append("images", imageNames);
        formData.append("leaveType", this.leave_data.leaveTypeId.toString());
        formData.append("leaveTypeId", this.leave_data.leaveTypeId.toString());
        formData.append("approve_id_now", this.user_approve_all[0]);
        formData.append(
          "startDate",
          this.leave_data.startDate.replace("+07:00", ""),
        );
        formData.append("startHour", this.leave_start_hours);
        formData.append(
          "endDate",
          this.leave_data.endDate.replace("+07:00", ""),
        );
        formData.append("endHour", this.leave_end_hours);
        formData.append("type_count", this.day_select);
        formData.append("description", this.leave_data.description);
        formData.append("userId", this.leave_data.userId);
        formData.append("departmentId", this.leave_data.departmentId);
        formData.append("companyId", this.leave_data.companyId);
        formData.append("amount", this.leave_data.amount);
        formData.append("lq_id", leave_quota_list);
        let lq_id_v = "";
        avg_minutes_list.forEach((item, index) => {
          if (index == 0) {
            lq_id_v += `{ "lq_id_v": ${item.leave_quota_id}, "avg_minutes": ${item.avg_minutes}}`;
          } else {
            lq_id_v += `|{ "lq_id_v": ${item.leave_quota_id}, "avg_minutes": ${item.avg_minutes}}`;
          }
        });
        formData.append("lq_id_v", lq_id_v);
        const res_h = await api.addLeaveHistory(formData);
        if (res_h.status == 200 || res_h.status == 201) {
          for await (const [index, item] of this.user_approve_all.entries()) {
            var data = {
              leave_his_id: res_h.data.module_id,
              approve_id: item,
            };
            if (item != 0 && item != null) {
              await api.addLeaveApproveDetail(data);
            }
            if (index == 0) {
              api.SendnotificationToMobile({
                user_id: item,
                send_mode: "send_to_approver",
              });
            }
          }

          this.$store.state.global_push_dialog = true;
          this.setupAlertDialogPush(
            true,
            "Request Submitted",
            "You have submitted this request.",
            "text-h5 green--text text-center",
            "/my-time-off",
          );
        } else {
          this.$store.state.global_dialog = true;
          this.setupAlertDialog(
            true,
            "Information Saved",
            "บันทึกข้อมูลไม่สำเร็จ",
            "text-h5 red--text text-center",
          );
        }
        this.$hideLoader();
      } else {
        return;
      }
    },
    setupAlertDialog(status, title, message, text_color) {
      this.title = title;
      this.message = message;
      this.dialogAdd = status;
      this.text_color = text_color;
    },
    setupAlertDialogPush(status, title, message, text_color, link) {
      this.title = title;
      this.message = message;
      this.dialogAdd = status;
      this.text_color = text_color;
      this.link = link;
    },
    saveItemConfirm() {
      this.dialogSave = true;
    },
    closeSave() {
      this.dialogSave = false;
    },
    deleteItemConfirm(index) {
      this.indeximage = index;
      this.dialogDelete = true;
    },
    closeDelete() {
      this.indeximage = null;
      this.dialogDelete = false;
    },
    async removeimage(index) {
      this.preview_list.splice(this.indeximage, 1);
      this.image_list.splice(this.indeximage, 1);

      this.dialogDelete = false;
    },
    async loaddataLeaveUsed(id) {
      const result = await api.getListUseByUserIDV2byCompany(
        id,
        localStorage.getItem(server.COMPANYID),
      );

      // console.log("loaddataLeaveUsed result:", JSON.stringify(result.data));
      //console.log(result.data);
      // alert(result.data.length);

      if (result.data.length <= 0) {
        this.user_approve_count_status2 = false;
        this.$store.state.global_dialog = true;
        this.setupAlertDialog(
          true,
          "No Time Off",
          "You do not have any time off permitted.",
          "text-h5 red--text text-center",
        );
        return;
      } else {
        this.user_approve_count_status2 = true;
      }

      var data = [];

      result.data.forEach((item) => {
        var obj = {
          id: 0,
          leave_type_id: 1,
          use_leave: "0.0625",
          leave_period_from: "2021-12-31T17:00:00.000Z",
          leave_period_to: "2022-12-30T17:00:00.000Z",
          leave_type: "ลาป่วย",
          quota_day: "30.0000",
          user_day: "0.0625",
          balance: "29.9375",
        };

        obj.id = item.id;
        obj.leave_type_id = item.leave_type_id;
        obj.use_leave = item.use_leave;
        obj.leave_period_from = item.leave_period_from;
        obj.leave_period_to = item.leave_period_to;
        obj.period = item.period;
        obj.leave_type = item.leave_type;
        // obj.quota_day = item.quota_day;
        // obj.quota_day = this.quotaListConvert(item.quota_day);
        obj.user_day = this.quotaListConvert(item.user_day);
        obj.balance_show = this.quotaListConvert(item.balance);
        obj.quota_day_show = this.quotaListConvert(item.quota_day);
        obj.balance = item.balance;
        obj.quota_day = item.quota_day;
        // obj.balance = this.quotaListConvert(item.balance);

        data.push(obj);
      });

      this.dataAll = data;

      const result_dropsown = await api.getListByUserIDV2byCompany(
        id,
        localStorage.getItem(server.COMPANYID),
      );
      this.dropdownLeave = result_dropsown.data;

      // this.$showLoader();
      // this.$hideLoader();
    },
    quotaListConvert: function (H) {
      var d = 0;
      var h = 0;
      var m = 0;
      var tmp1 = parseFloat(H * 480);
      var tmp_final;

      var tmp_d = tmp1 / 480;

      d = Math.floor(tmp_d);
      h = Math.floor((tmp_d - d) * 8);
      m = Math.floor(((tmp_d - d) * 8 - h) * 60);

      tmp_final = `${d} days ${h} hour`;

      return tmp_final;
    },

    previewMultiImage: function (event) {
      var input = event.target;
      var count = input.files.length;
      var index = 0;
      if (input.files) {
        while (count--) {
          var reader = new FileReader();
          reader.onload = (e) => {
            this.preview_list.push(e.target.result);
          };
          if (input.files[index].type.split("/")[0] === "image") {
            this.image_list.push(input.files[index]);
            reader.readAsDataURL(input.files[index]);
          }
          index++;
        }
      }

    },
  },
};
</script>

<style>
.heightsize80 {
  height: 85px;
}

.v-btn:not(.v-btn--round).v-size--default.buttonblue {
  background-color: #1d2442;
  color: white;
}

.heightsize55 {
  height: 55px;
}

.heightsize30 {
  height: 30px;
}

.inputsize {
  font-size: smaller;
}

.btnuploadfile {
  border: 2px solid;
}

.border-dt-picker {
  border: 2px solid;
  border-radius: 5px;
  height: 45px;
}

.heightsize70formadvance {
  height: 70px;
}
</style>

<style scoped>
/* ================= Page ================= */
.rq-page {
  padding: 16px 32px 32px;
  color: #1F2937;
}

/* ดึงหัวข้อขึ้นไปอยู่ระดับเดียวกับ Header (ตามรูปต้นแบบ) */
.rq-header {
  margin-top: -72px;
}

.rq-back {
  position: relative;
  z-index: 6;
  /* อยู่เหนือ v-app-bar (z-index 5) เพื่อให้คลิกได้ */
  display: inline-flex;
  align-items: center;
  color: #2A6F97 !important;
  font-size: 15px;
  cursor: pointer;
  text-decoration: none;
}

.rq-back:hover {
  text-decoration: underline;
}

.rq-title {
  font-size: 30px;
  font-weight: 700;
  color: #111827;
  line-height: 1.2;
  margin: 14px 0 0;
}

.rq-subtitle {
  font-size: 15px;
  color: #4B5563;
  margin-top: 6px;
}

.rq-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 28px;
  margin-top: 24px;
  align-items: start;
}

/* ================= Card ================= */
.rq-card {
  background: #FFFFFF;
  border: 1px solid #E8ECF1;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}

.rq-form-body {
  padding: 24px 24px 20px;
}

.rq-card-title {
  font-size: 19px;
  font-weight: 700;
  color: #111827;
}

.rq-muted-sm {
  font-size: 13px;
  color: #6B7280;
  margin-top: 2px;
}

.rq-req {
  color: #D93025;
}

.rq-label {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 6px;
}

.rq-help {
  font-size: 12px;
  color: #6B7280;
  margin-top: 6px;
}

/* ================= Inputs ================= */
.rq-input {
  font-size: 14px;
}

.rq-input>>>.v-input__slot {
  min-height: 42px !important;
  border-radius: 6px !important;
  background: #FFFFFF !important;
}

.rq-input>>>fieldset {
  border-color: #D1D5DB !important;
}

.rq-input.v-input--is-focused>>>fieldset {
  border-color: #2A6F97 !important;
  border-width: 2px !important;
}

.rq-textarea>>>textarea {
  font-size: 14px;
}

.rq-row {
  display: flex;
  gap: 20px;
}

.rq-col {
  flex: 1 1 0;
  min-width: 0;
}

/* ================= Toggle ================= */
.rq-toggle {
  display: flex;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  background: #F9FAFB;
  overflow: hidden;
}

.rq-toggle-btn {
  flex: 1 1 0;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  color: #374151;
  background: transparent;
  transition: background-color 0.2s ease;
}

.rq-toggle-active {
  background: #2A6F97;
  color: #FFFFFF;
  font-weight: 500;
}

/* ================= Info bar ================= */
.rq-info-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  background: #F2F8FC;
  border: 1px solid #DCEAF4;
  border-radius: 8px;
  padding: 10px 18px;
  font-size: 14px;
  color: #374151;
}

.rq-info-icon {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #2A6F97;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rq-info-sep {
  width: 1px;
  height: 24px;
  background: #D1D5DB;
  margin-left: auto;
}

.rq-accent {
  color: #2A6F97;
  font-weight: 600;
}

.rq-over {
  color: #D93025 !important;
}

.rq-accent-lg {
  font-size: 17px;
}

.rq-divider {
  border-color: #EEF1F4 !important;
}

/* ================= Attachment ================= */
.rq-drop {
  display: flex;
  align-items: center;
  border: 1.5px dashed #CBD5E1;
  border-radius: 8px;
  padding: 18px 24px;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.rq-drop:hover,
.rq-drop-over {
  background: #F2F8FC;
  border-color: #2A6F97;
}

.rq-drop-title {
  font-size: 15px;
  color: #111827;
}

.rq-link {
  color: #2A6F97 !important;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
}

.rq-link:hover {
  text-decoration: underline;
}

.rq-preview-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 14px;
}

.rq-preview-item {
  position: relative;
  width: 88px;
  height: 88px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
}

.rq-preview-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.rq-preview-remove {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(17, 24, 39, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ================= Footer ================= */
.rq-form-footer {
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #EEF1F4;
  padding: 16px 24px;
}

.rq-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-size: 15px !important;
  font-weight: 600 !important;
  height: 44px !important;
  min-width: 140px !important;
  border-radius: 6px !important;
}

.rq-btn-outline {
  border-color: #E5E7EB !important;
  color: #111827 !important;
}

.rq-btn-primary {
  color: #FFFFFF !important;
  min-width: 190px !important;
}

/* ================= Right column ================= */
.rq-side-card {
  padding: 24px;
}

.rq-sum-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: #374151;
  padding: 8px 0;
}

.rq-sum-row> :last-child {
  text-align: right;
}

.rq-sum-row> :last-child:not(.rq-accent) {
  color: #111827;
}

.rq-note {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #4B5563;
}

.rq-yto {
  display: flex;
  align-items: stretch;
}

.rq-yto-item {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-width: 0;
}

.rq-yto-sep {
  width: 1px;
  background: #E5E7EB;
}

.rq-yto-label {
  font-size: 14px;
  color: #374151;
  margin-top: 10px;
}

.rq-yto-value {
  font-size: 22px;
  font-weight: 600;
  line-height: 1.3;
  margin-top: 4px;
}

.rq-yto-unit {
  font-size: 15px;
  font-weight: 500;
}

.rq-yto-sub {
  display: block;
  font-size: 12px;
  font-weight: 500;
}

.rq-history-link {
  display: inline-flex;
  align-items: center;
  font-size: 15px;
  font-weight: 600;
}

/* ================= Responsive ================= */
@media (max-width: 1100px) {
  .rq-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .rq-page {
    padding: 8px 12px 24px;
  }

  .rq-header {
    margin-top: 0;
  }

  .rq-row {
    flex-direction: column;
    gap: 16px;
  }

  .rq-info-sep {
    display: none;
  }
}
</style>